import { type ComputedRef, type Ref, onBeforeUnmount, ref, watch } from 'vue';
import {
	TRACKING_SUCCESS_FADE_MS,
	TRACKING_SUCCESS_MAX_WAIT_MS,
	TRACKING_SUCCESS_MIN_MS,
} from '../../constants';

export function useTrackingSuccessFlash({
	isTrackingThisTaskFromApollo,
	totalDurationSeconds,
	isRequiredFieldsMissing,
}: {
	isTrackingThisTaskFromApollo: ComputedRef<boolean>;
	totalDurationSeconds: Ref<number>;
	isRequiredFieldsMissing: ComputedRef<boolean>;
}) {
	const trackingSuccessFlash = ref(false);
	const trackingSuccessLeaving = ref(false);
	const frozenDurationSeconds = ref<number | null>(null);
	const trackingPausedForTimeEntryModal = ref(false);

	let trackingSuccessFlashStartedAt = 0;
	let trackingSuccessFlashEndTimeoutId:
		| ReturnType<typeof setTimeout>
		| undefined;
	let trackingSuccessFlashMaxWaitTimeoutId:
		| ReturnType<typeof setTimeout>
		| undefined;
	let trackingSuccessFadeTimeoutId: ReturnType<typeof setTimeout> | undefined;

	const clearTrackingSuccessFlashState = () => {
		trackingSuccessFlash.value = false;
		trackingSuccessLeaving.value = false;
		frozenDurationSeconds.value = null;

		if (trackingSuccessFlashEndTimeoutId !== undefined) {
			clearTimeout(trackingSuccessFlashEndTimeoutId);
			trackingSuccessFlashEndTimeoutId = undefined;
		}

		if (trackingSuccessFlashMaxWaitTimeoutId !== undefined) {
			clearTimeout(trackingSuccessFlashMaxWaitTimeoutId);
			trackingSuccessFlashMaxWaitTimeoutId = undefined;
		}

		if (trackingSuccessFadeTimeoutId !== undefined) {
			clearTimeout(trackingSuccessFadeTimeoutId);
			trackingSuccessFadeTimeoutId = undefined;
		}
	};

	const runTrackingSuccessFadeOutAndClear = () => {
		if (trackingSuccessFadeTimeoutId !== undefined) {
			clearTimeout(trackingSuccessFadeTimeoutId);
		}

		trackingSuccessLeaving.value = true;
		trackingSuccessFadeTimeoutId = setTimeout(() => {
			trackingSuccessFadeTimeoutId = undefined;
			trackingSuccessLeaving.value = false;
			clearTrackingSuccessFlashState();
		}, TRACKING_SUCCESS_FADE_MS);
	};

	const scheduleTrackingSuccessFlashEnd = () => {
		const elapsed = Date.now() - trackingSuccessFlashStartedAt;
		const delay = Math.max(0, TRACKING_SUCCESS_MIN_MS - elapsed);
		if (trackingSuccessFlashEndTimeoutId !== undefined) {
			clearTimeout(trackingSuccessFlashEndTimeoutId);
		}

		trackingSuccessFlashEndTimeoutId = setTimeout(() => {
			trackingSuccessFlashEndTimeoutId = undefined;
			runTrackingSuccessFadeOutAndClear();
		}, delay);
	};

	watch(isTrackingThisTaskFromApollo, (tracking) => {
		if (tracking) {
			return;
		}

		if (trackingSuccessFlash.value) {
			trackingPausedForTimeEntryModal.value = false;
			if (trackingSuccessFlashMaxWaitTimeoutId !== undefined) {
				clearTimeout(trackingSuccessFlashMaxWaitTimeoutId);
				trackingSuccessFlashMaxWaitTimeoutId = undefined;
			}

			scheduleTrackingSuccessFlashEnd();
			return;
		}

		frozenDurationSeconds.value = null;
	});

	const onComplete = (): { openModalOnly: boolean } => {
		frozenDurationSeconds.value = totalDurationSeconds.value;

		if (isRequiredFieldsMissing.value) {
			trackingPausedForTimeEntryModal.value = true;
			return { openModalOnly: true };
		}

		trackingSuccessFlashStartedAt = Date.now();
		trackingSuccessLeaving.value = false;
		trackingSuccessFlash.value = true;

		if (trackingSuccessFlashMaxWaitTimeoutId !== undefined) {
			clearTimeout(trackingSuccessFlashMaxWaitTimeoutId);
		}

		trackingSuccessFlashMaxWaitTimeoutId = setTimeout(() => {
			trackingSuccessFlashMaxWaitTimeoutId = undefined;
			if (
				trackingSuccessFlash.value &&
				isTrackingThisTaskFromApollo.value
			) {
				runTrackingSuccessFadeOutAndClear();
			}
		}, TRACKING_SUCCESS_MAX_WAIT_MS);

		return { openModalOnly: false };
	};

	const freezeDuration = () => {
		frozenDurationSeconds.value = totalDurationSeconds.value;
	};

	const pauseForModal = () => {
		trackingPausedForTimeEntryModal.value = true;
	};

	const onModalClosed = () => {
		if (!trackingPausedForTimeEntryModal.value) {
			return;
		}

		trackingPausedForTimeEntryModal.value = false;
		if (!trackingSuccessFlash.value) {
			frozenDurationSeconds.value = null;
		}
	};

	onBeforeUnmount(() => {
		clearTrackingSuccessFlashState();
	});

	return {
		trackingSuccessFlash,
		trackingSuccessLeaving,
		frozenDurationSeconds,
		trackingPausedForTimeEntryModal,
		onComplete,
		freezeDuration,
		pauseForModal,
		onModalClosed,
	};
}
